import { useTranslation } from 'react-i18next';
import { AvatarForm } from '../../avatar/avatar-form';
import {
  DEFAULT_BIRTH_MONTH,
  isDisabledBirthDate,
  latestAdultBirthDate,
} from '../../datepicker/birth-date';
import { DatePickerForm } from '../../datepicker/datepicker-form';
import { selectUserInitials } from '../../edit-admin-profile/edit-admin-profile-form';
import { FormSection, FormSectionFullWidth } from '../../form-section';
import { ProfileTextField } from '../profile-text-field';
import { type DoctorProfileFormMode, isFieldVisible } from '../types';
import type { DoctorProfileUseForm } from '../use-doctor-profile-form';

/**
 * "Identité" section: avatar, names, phone and date of birth.
 *
 * @param props.form - the unified profile form
 * @param props.mode - the form mode driving field visibility
 * @param props.onDeleteAvatar - called when the avatar is removed
 * @returns the section
 */
export const IdentitySection = ({
  form,
  mode,
  onDeleteAvatar,
}: {
  form: DoctorProfileUseForm;
  mode: DoctorProfileFormMode;
  onDeleteAvatar?: () => void;
}) => {
  const { t } = useTranslation();
  // Watched so the avatar initials follow the name while it is edited.
  const [picture, firstname, lastname] = form.watch([
    'picture',
    'firstname',
    'lastname',
  ]);
  const { initials } = selectUserInitials({ firstname, lastname });
  const now = new Date();

  return (
    <FormSection title={t('edit-user-profile-form.section.identity')}>
      {isFieldVisible(mode, 'avatar') && (
        <FormSectionFullWidth>
          <AvatarForm
            form={form}
            fieldName="file"
            currentAvatar={picture}
            placeholder={initials}
            onDeleteAvatar={() => {
              form.setValue('picture', null);
              onDeleteAvatar?.();
            }}
          />
        </FormSectionFullWidth>
      )}
      <ProfileTextField
        form={form}
        name="lastname"
        labelKey="edit-user-profile-form.lastname.label"
      />
      <ProfileTextField
        form={form}
        name="firstname"
        labelKey="edit-user-profile-form.firstname.label"
      />
      <ProfileTextField
        form={form}
        name="phoneNumber"
        type="tel"
        labelKey="edit-user-profile-form.phone-number.label"
        placeholderKey="edit-user-profile-form.phone-number.placeholder"
      />
      {isFieldVisible(mode, 'dateOfBirth') && (
        <DatePickerForm
          form={form}
          fieldName="dateOfBirth"
          label={t('edit-user-profile-form.date-of-birth.label')}
          captionLayout="dropdown"
          disabled={(date: Date) => isDisabledBirthDate({ date, now })}
          defaultMonth={DEFAULT_BIRTH_MONTH}
          endMonth={latestAdultBirthDate(now)}
        />
      )}
    </FormSection>
  );
};
